// Appistry Idea Discovery Form — Jun 2026 schema (v2)
// All sections, options and field keys for the IdeaLab wizard.
// Field `k` keys map 1:1 to backend DiscoveryAnswers Pydantic model.

// Step-0 qualifier — sets context so the AI follow-ups stay relevant
// (a local community club doesn't need formal B2B questions).
export const ORGANISATION_TYPES = [
  "Business",
  "Club or community group",
  "Social enterprise",
  "Charity",
  "Individual / sole trader",
  "Not sure yet",
];

export const PROJECT_TYPES = [
  "Mobile or web app",
  "SaaS product",
  "Managed online presence (website + email + basics)",
  "Internal tool",
  "Not sure yet",
];

export const BUDGET_STATEMENTS = [
  "I'm exploring ideas and don't yet know what a project like this might cost.",
  "I have a limited budget and would like to start small.",
  "I have a budget in mind and would like recommendations within it.",
  "I'm looking for the right solution rather than working to a fixed budget.",
];

export const BUDGET_BANDS = [
  "Under £1,000",
  "£1,000–£2,500",
  "£2,500–£5,000",
  "£5,000–£10,000",
  "£10,000–£20,000",
  "£20,000+",
  "Prefer not to say",
];

export const RADIO_OPTIONS = {
  customer_location: ["UK", "Europe", "Worldwide", "Other"],
  has_existing_content: ["Yes", "Partially", "No"],
  will_continue_creating: ["Yes", "No", "Not sure"],
  content_amount: ["Small amount", "Moderate amount", "Large library", "Not sure"],
  access_model: ["Completely free", "Subscription", "One-off purchases", "Mixture of free and paid", "Not sure"],
  yes_no_not_sure: ["Yes", "No", "Not sure"],
  yes_no: ["Yes", "No"],
  domain_manager: ["Me", "Agency", "Friend / Family", "Previous supplier", "Not sure"],
};

export const MULTI_OPTIONS = {
  customer_audience: ["Existing customers", "Mailing list", "Social media following", "Community", "Website visitors", "None yet"],
  offerings: ["Videos", "Audio", "Guided sessions", "Courses", "Programmes", "Downloads", "PDFs", "Worksheets", "Assessments", "Community features", "Appointment booking", "Events", "Other"],
  signin_methods: ["Email & password", "Google", "Apple", "Microsoft", "Not sure"],
  ai_features: ["Recommend relevant content", "Suggest tailored journeys", "Surface what each person needs next", "Answer questions about your content", "Personalise the experience", "Not sure", "No AI required"],
  existing_setup: ["Website", "Domain name", "Professional email address", "Social media channels", "Mailing list", "Online payments", "None"],
  brand_feelings: ["Calm", "Supported", "Inspired", "Professional", "Empowered", "Premium", "Other"],
  brand_assets: ["Logo", "Colour palette", "Website", "Brand guidelines", "Marketing materials", "Mood board"],
};

export const SECTIONS = [
  {
    id: "section-1",
    label: "About your business",
    title: "Tell us about you and your business.",
    intro: "A few open questions so we can understand the shape of your idea before we ask the detail.",
    questions: [
      { k: "business_description", type: "longtext", label: "Describe your business", hint: "What do you do and who do you help?", placeholder: "A short sentence or two — what you do day to day, who you do it for, and what's special about how you do it." },
      { k: "idea_description",     type: "longtext", label: "Tell us about your idea", hint: "What are you hoping to create?", placeholder: "In plain English — what should this thing actually do? Who's it for, and how do they use it?" },
      { k: "inspiration",          type: "longtext", label: "What inspired this idea?", placeholder: "What problem keeps coming up, or what opportunity have you spotted? A real moment or recurring frustration is gold here." },
      { k: "success_vision",       type: "longtext", label: "What would success look like 12 months after launch?", placeholder: "Numbers if you have them, feelings if you don't. What would tell you it's working?" },
      { k: "top_three_v1",         type: "longtext", label: "If we could only deliver THREE things in Version 1, what would they be?", placeholder: "1.\n2.\n3.\n\nThree is enough — keep it focused." },
    ],
  },
  {
    id: "section-2",
    label: "Your customers",
    title: "Who is this really for?",
    intro: "The clearer the picture of your customer, the better we can shape the experience.",
    questions: [
      { k: "customer_who",         type: "longtext", label: "Who is this for?", placeholder: "The people who'll use this most. A rough age range, where they are, and how they'd describe themselves all help us picture them." },
      { k: "customer_challenges",  type: "longtext", label: "What challenges do they face?" },
      { k: "customer_outcomes",    type: "longtext", label: "What outcome are they hoping to achieve?" },
      { k: "customer_location",    type: "choice",   label: "Where are your customers based?", options: "customer_location" },
      { k: "customer_audience",    type: "multi",    label: "Do you already have an audience?", hint: "Pick all that apply.", options: "customer_audience" },
    ],
  },
  {
    id: "section-3",
    label: "What you'll offer",
    title: "What would you like to offer?",
    intro: "Pick everything that sounds relevant — we'll dig into specifics later.",
    questions: [
      { k: "offerings",              type: "multi",  label: "Tick everything that sounds relevant", options: "offerings" },
      { k: "has_existing_content",   type: "choice", label: "Do you already have this content?", options: "has_existing_content" },
      { k: "will_continue_creating", type: "choice", label: "Will you continue creating content after launch?", options: "will_continue_creating" },
      { k: "content_amount",         type: "choice", label: "Approximately how much content already exists?", options: "content_amount" },
    ],
  },
  {
    id: "section-4",
    label: "Access & pricing",
    title: "How should people access it?",
    intro: "Quick choices on access and accounts — you can change your mind later.",
    questions: [
      { k: "access_model",      type: "choice", label: "How would you like people to access your content?", options: "access_model" },
      { k: "membership_levels", type: "choice", label: "Would you like different membership levels?", options: "yes_no_not_sure" },
      { k: "user_accounts",     type: "choice", label: "Will users need accounts?", options: "yes_no_not_sure" },
      { k: "signin_methods",    type: "multi",  label: "How would you like people to sign in?", options: "signin_methods" },
    ],
  },
  {
    id: "section-5",
    label: "AI & personalisation",
    title: "AI & personalisation",
    intro: "Optional features that turn a standard app into something that feels personal. Skip any that don't apply.",
    questions: [
      { k: "ai_features",        type: "multi",  label: "Would it be useful if the platform could:", options: "ai_features" },
      { k: "ai_content_support", type: "choice", label: "Do you already have content that could support this?", options: "yes_no_not_sure" },
    ],
  },
  {
    id: "section-6",
    label: "Existing setup",
    title: "What's already in place?",
    intro: "Tick everything you already have. We'll only ask follow-ups where they matter.",
    questions: [
      { k: "existing_setup",          type: "multi",  label: "Do you currently have any of the following?", options: "existing_setup" },

      // Branching: Domain
      { k: "domain_name",             type: "text",   label: "What is your domain name?", placeholder: "yourdomain.co.uk",
        dependsOn: { field: "existing_setup", value: "Domain name" } },
      { k: "domain_manager",          type: "choice", label: "Do you know who manages it?", options: "domain_manager",
        dependsOn: { field: "existing_setup", value: "Domain name" } },
      { k: "domain_access",           type: "choice", label: "Do you have access to it?", options: "yes_no_not_sure",
        dependsOn: { field: "existing_setup", value: "Domain name" } },

      // Branching: Professional Email
      { k: "pro_email_wanted",        type: "choice", label: "Would you like professional email addresses associated with your domain?", options: "yes_no_not_sure",
        dependsOn: { field: "existing_setup", value: "Professional email address" } },

      // Branching: Payments
      { k: "accepts_payments_online", type: "choice", label: "Do you currently accept payments online?", options: "yes_no",
        dependsOn: { field: "existing_setup", value: "Online payments" } },
      { k: "payment_platform",        type: "text",   label: "Which platform do you use?", placeholder: "Stripe, PayPal, Square, GoCardless…",
        dependsOn: { field: "existing_setup", value: "Online payments" } },
    ],
  },
  {
    id: "section-7",
    label: "Brand & style",
    title: "How should it look and feel?",
    intro: "Even rough thoughts here help us design something that feels authentically you.",
    questions: [
      { k: "brand_three_words",     type: "text",     label: "Describe your brand in three words", placeholder: "Three words, comma separated. No wrong answers." },
      { k: "brand_feelings",        type: "multi",    label: "How should people feel when using your platform?", options: "brand_feelings" },
      { k: "brand_assets",          type: "multi",    label: "Do you already have any of the following?", options: "brand_assets" },
      { k: "brand_colors_like",     type: "text",     label: "Colours you like", placeholder: "Names, hex codes, or just the vibe — whatever's easiest." },
      { k: "brand_colors_dislike",  type: "text",     label: "Colours you dislike", placeholder: "Anything that's an absolute no for your brand." },
      { k: "brand_inspirations",    type: "longtext", label: "Are there any websites or apps you admire?", placeholder: "Links or names work fine — and tell us what you like about each, even if it's a single word." },
    ],
  },
  {
    id: "section-8",
    label: "Budget & timing",
    title: "Budget & next steps",
    intro: "Approximate is fine — we use this to scope the right next step, not to pin you down.",
    questions: [
      { k: "budget_statement",   type: "budget-statements", label: "Which statement feels closest?" },
      { k: "budget_band",        type: "budget-bands",      label: "If you're comfortable sharing, what level of investment feels realistic?" },
      { k: "target_launch_date", type: "date",              label: "Do you have a target launch date?" },
    ],
  },
];
